<template>
    <div class="bg-white dark:bg-slate-800 rounded-lg p-4 mb-4 border border-gray-200 dark:border-gray-700">
        
        <!-- Loading State -->
        <div v-if="loading || isAnalyzing" class="flex flex-col items-center justify-center py-8">
            <ticto-loading v-if="!isAnalyzing" />
            <div v-if="isAnalyzing" class="w-full max-w-md">
                <div class="mb-4 text-center">
                    <span class="text-gray-600 dark:text-gray-400 text-sm font-medium">{{ analysisMessage }}</span>
                </div>
                <div class="relative">
                    <!-- Background track -->
                    <div class="overflow-hidden h-3 text-xs flex rounded-full bg-gray-200 dark:bg-gray-700">
                        <!-- Progress bar with gradient -->
                        <div 
                            class="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-500 ease-out"
                            :style="`width: ${analysisProgress}%`"
                        >
                            <!-- Animated stripes overlay -->
                            <div class="absolute inset-0 opacity-25">
                                <div class="h-full w-full bg-stripes animate-slide"></div>
                            </div>
                        </div>
                    </div>
                    <!-- Percentage display -->
                    <div class="mt-2 flex justify-between items-center">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Processando...</span>
                        <span class="text-sm font-bold text-indigo-600 dark:text-indigo-400">{{ analysisProgress }}%</span>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
            <div class="flex items-center">
                <ExclamationTriangleIcon class="h-5 w-5 text-red-600 mr-2" />
                <span class="text-red-800 dark:text-red-300">Erro ao carregar análise AI</span>
            </div>
        </div>
        
        <!-- AI Analysis Content -->
        <div v-else-if="aiAnalysis">
            <!-- Cabeçalho com Score de Risco e Sugestão -->
            <div class="flex items-center justify-between mb-4">
                <div class="flex items-center space-x-3">
                    <div class="flex items-center space-x-2">
                        <div class="w-4 h-4 rounded-full" :class="getRiskColor()"></div>
                        <div class="flex flex-col">
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Score AI
                            </span>
                            <div class="flex items-center space-x-2">
                                <span class="text-lg font-bold" :class="getTextRiskColor()">{{ aiAnalysis.score }}/100</span>
                                <span class="text-xs px-2 py-1 rounded-full font-medium" 
                                      :class="getRiskBadgeClass()">
                                    RISCO {{ getRiskLevel() }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <!-- Sugestão de Ação -->
                <div class="flex items-center space-x-2 text-right">
                    <div class="flex flex-col items-end">
                        <span class="text-xs text-gray-500 dark:text-gray-400 uppercase">Recomendação AI</span>
                        <div class="flex items-center space-x-1">
                            <component :is="getSuggestionIcon()" 
                                      class="h-4 w-4" 
                                      :class="getSuggestionIconColor()" />
                            <span class="text-sm font-bold" :class="getSuggestionTextColor()">
                                {{ getSuggestion() }}
                            </span>
                        </div>
                        <span class="text-xs text-gray-500 dark:text-gray-400">
                            Confiança: {{ Math.round(aiAnalysis.confidence * 100) }}%
                        </span>
                    </div>
                </div>
            </div>

            <!-- LLM Info Badge -->
            <div v-if="aiAnalysis.llm_used" class="mb-3">
                <span class="text-xs px-2 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300 rounded-full">
                    ✨ Análise com {{ aiAnalysis.llm_provider }} {{ aiAnalysis.llm_model }}
                </span>
            </div>

            <!-- Métricas Críticas -->
            <div v-if="getInsightMetrics().critical.length > 0" class="mb-4">
                <div class="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase font-medium">🔴 Fatores Críticos</div>
                <div class="flex flex-wrap gap-2">
                    <span v-for="metric in getInsightMetrics().critical" 
                          :key="metric"
                          class="text-xs px-2 py-1 rounded-full font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                        {{ humanizeMetric(metric) }}
                    </span>
                </div>
            </div>

            <!-- Métricas de Alerta -->
            <div v-if="getInsightMetrics().warning.length > 0" class="mb-4">
                <div class="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase font-medium">⚠️ Alertas</div>
                <div class="flex flex-wrap gap-2">
                    <span v-for="metric in getInsightMetrics().warning" 
                          :key="metric"
                          class="text-xs px-2 py-1 rounded-full font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                        {{ humanizeMetric(metric) }}
                    </span>
                </div>
            </div>

            <!-- Indicadores Positivos -->
            <div v-if="getInsightMetrics().positive.length > 0" class="mb-4">
                <div class="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase font-medium">✅ Indicadores Positivos</div>
                <div class="flex flex-wrap gap-2">
                    <span v-for="metric in getInsightMetrics().positive" 
                          :key="metric"
                          class="text-xs px-2 py-1 rounded-full font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                        {{ humanizeMetric(metric) }}
                    </span>
                </div>
            </div>

            <!-- Padrões Detectados -->
            <div v-if="aiAnalysis.patterns_detected && aiAnalysis.patterns_detected.length > 0" class="mb-4">
                <div class="text-xs text-gray-500 dark:text-gray-400 mb-2 uppercase font-medium">🔍 Padrões Detectados</div>
                <div class="space-y-1">
                    <div v-for="pattern in aiAnalysis.patterns_detected" 
                         :key="pattern"
                         class="text-xs text-gray-700 dark:text-gray-300">
                        • {{ humanizePattern(pattern) }}
                    </div>
                </div>
            </div>

            <!-- Recomendações -->
            <div v-if="aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0" class="mb-4 bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
                <div class="text-xs text-gray-700 dark:text-gray-300 font-medium mb-1">💡 Recomendações AI:</div>
                <ul class="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    <li v-for="(rec, index) in aiAnalysis.recommendations.slice(0, 3)" :key="index">
                        • {{ rec }}
                    </li>
                </ul>
            </div>

            <!-- Informações Detalhadas -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div class="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                    <div class="text-lg font-bold" :class="getTimeColor()">
                        {{ getTimeCategory() }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Horário da Solicitação</div>
                </div>
                
                <div class="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded">
                    <div class="text-lg font-bold text-gray-700 dark:text-gray-300">
                        {{ getFormattedCreationTime() }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Solicitado há</div>
                </div>
            </div>

            <!-- Ações Rápidas -->
            <div class="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-600">
                <div class="flex space-x-2">
                    <button @click="refreshAnalysis" 
                            :disabled="refreshing"
                            class="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300">
                        {{ refreshing ? 'Atualizando...' : '🔄 Atualizar Análise AI' }}
                    </button>
                    
                    <button @click="showRawData = true"
                            class="text-xs text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300">
                        📊 Ver Dados Completos
                    </button>
                </div>
                
                <div class="flex space-x-2">
                    <button v-if="canQuickApprove()" 
                            @click="$emit('quick-approve')"
                            class="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                        ✓ Aprovar Rápido
                    </button>
                    
                    <button v-if="shouldReject()" 
                            @click="$emit('quick-reject')"
                            class="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors">
                        ✗ Rejeitar
                    </button>
                </div>
            </div>
        </div>
        
        <!-- Fallback when no AI analysis exists -->
        <div v-else>
            <div class="text-center py-4 text-gray-500">
                <ExclamationTriangleIcon class="h-8 w-8 mx-auto mb-2" />
                <p class="text-sm">Análise AI não disponível</p>
                <div class="mt-4 space-y-2">
                    <button @click="generateAnalysis" 
                            :disabled="isAnalyzing"
                            class="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        🤖 Gerar Análise AI
                    </button>
                    <p class="text-xs text-gray-400">
                        Clique para gerar uma análise inteligente deste saque
                    </p>
                </div>
            </div>
        </div>
        
        <!-- Modal de Dados Raw -->
        <div v-if="showRawData" class="fixed inset-0 z-50 overflow-y-auto">
            <div class="flex items-center justify-center min-h-screen px-4 text-center">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showRawData = false"></div>
                
                <div class="relative bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:max-w-4xl sm:w-full">
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4 dark:text-white">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                                Dados Completos da Análise AI
                            </h3>
                            <button @click="showRawData = false" class="text-gray-400 hover:text-gray-500">
                                <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                        
                        <div class="mt-2 max-h-96 overflow-y-auto space-y-4">
                            <!-- Tabs -->
                            <div class="border-b border-gray-200 dark:border-gray-700">
                                <nav class="-mb-px flex space-x-8">
                                    <button @click="activeTab = 'analysis'"
                                            :class="activeTab === 'analysis' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                                            class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm">
                                        Análise Completa
                                    </button>
                                    <button @click="activeTab = 'request'"
                                            :class="activeTab === 'request' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                                            class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm">
                                        Dados Enviados
                                    </button>
                                    <button v-if="aiAnalysis.llm_prompt" @click="activeTab = 'prompt'"
                                            :class="activeTab === 'prompt' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                                            class="whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm">
                                        Prompt LLM
                                    </button>
                                </nav>
                            </div>
                            
                            <!-- Tab Content -->
                            <div v-if="activeTab === 'analysis'">
                                <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">JSON da Análise AI:</h4>
                                <pre class="text-xs bg-gray-100 dark:bg-gray-900 p-4 rounded overflow-x-auto">{{ JSON.stringify(aiAnalysis, null, 2) }}</pre>
                            </div>
                            
                            <div v-if="activeTab === 'request'">
                                <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dados do Cashout Analisados:</h4>
                                <pre class="text-xs bg-gray-100 dark:bg-gray-900 p-4 rounded overflow-x-auto">{{ JSON.stringify(aiAnalysis.request_data || {}, null, 2) }}</pre>
                            </div>
                            
                            <div v-if="activeTab === 'prompt' && aiAnalysis.llm_prompt">
                                <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Prompt Enviado para {{ aiAnalysis.llm_provider }} ({{ aiAnalysis.llm_model }}):</h4>
                                <pre class="text-xs bg-gray-100 dark:bg-gray-900 p-4 rounded overflow-x-auto whitespace-pre-wrap">{{ aiAnalysis.llm_prompt }}</pre>
                            </div>
                        </div>
                    </div>
                    
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button @click="showRawData = false" 
                                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm">
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { 
    CheckCircleIcon, 
    ExclamationTriangleIcon, 
    XCircleIcon,
    ClockIcon 
} from '@heroicons/vue/24/outline';
import TictoLoading from '../../layouts/TictoLoading.vue';

export default {
    name: 'SmartAnalysis',
    components: {
        CheckCircleIcon,
        ExclamationTriangleIcon, 
        XCircleIcon,
        ClockIcon,
        TictoLoading
    },
    props: {
        review: {
            type: Object,
            required: true
        }
    },
    emits: ['quick-approve', 'quick-reject'],
    
    data() {
        return {
            loading: false,
            refreshing: false,
            error: null,
            aiAnalysis: null,
            showRawData: false,
            activeTab: 'analysis',
            analysisProgress: 0,
            analysisMessage: '',
            isAnalyzing: false
        };
    },
    
    mounted() {
        this.loadAIAnalysis();
        this.setupEchoListeners();
    },
    
    beforeUnmount() {
        this.cleanupEchoListeners();
    },
    
    methods: {
        setupEchoListeners() {
            // Listen to cashout-specific channel only
            if (window.Echo) {
                // Listen to specific cashout channel (now public)
                // Using only the specific channel to avoid duplicate events
                window.Echo.channel(`cashout.${this.review.cashout.id}`)
                    .listen('.analysis.started', (e) => {
                        console.log('Analysis started:', e);
                        this.isAnalyzing = true;
                        this.analysisProgress = 10;
                        this.analysisMessage = 'Iniciando análise com IA...';
                    })
                    .listen('.analysis.progress', (e) => {
                        console.log('Analysis progress:', e);
                        this.analysisProgress = e.progress;
                        this.analysisMessage = e.message;
                    })
                    .listen('.analysis.completed', (e) => {
                        console.log('Analysis completed:', e);
                        this.isAnalyzing = false;
                        this.analysisProgress = 100;
                        this.analysisMessage = 'Análise concluída!';
                        // Update the analysis data
                        if (e.analysis) {
                            this.aiAnalysis = e.analysis;
                        }
                        // Reload after a short delay to ensure data is saved
                        setTimeout(() => {
                            this.loadAIAnalysis();
                        }, 500);
                    })
                    .listen('.analysis.failed', (e) => {
                        console.log('Analysis failed:', e);
                        this.isAnalyzing = false;
                        this.error = e.error;
                        this.analysisMessage = 'Erro na análise: ' + e.error;
                        if (this.$toast) {
                            this.$toast.error('Erro na análise AI: ' + e.error);
                        }
                    });
            }
        },
        
        cleanupEchoListeners() {
            if (window.Echo) {
                window.Echo.leave(`cashout.${this.review.cashout.id}`);
            }
        },
        
        async loadAIAnalysis() {
            this.loading = true;
            this.error = null;
            
            try {
                const response = await axios.get(`/api/user/${this.review.cashout.id}/ai-analysis`);
                this.aiAnalysis = response.data.analysis;
            } catch (error) {
                console.error('Failed to load AI analysis:', error);
                this.error = error.message;
            } finally {
                this.loading = false;
            }
        },
        
        async generateAnalysis() {
            this.isAnalyzing = true;
            this.analysisProgress = 5;
            this.analysisMessage = 'Iniciando geração da análise AI...';
            
            try {
                const response = await axios.post(`/api/user/${this.review.cashout.id}/ai-analysis/regenerate`);
                this.aiAnalysis = response.data.analysis;
                
                if (this.$toast) {
                    this.$toast.success('Análise AI gerada com sucesso!');
                }
            } catch (error) {
                console.error('Failed to generate AI analysis:', error);
                this.error = error.response?.data?.message || 'Erro ao gerar análise AI';
                
                if (this.$toast) {
                    this.$toast.error('Erro ao gerar análise AI');
                }
            } finally {
                this.isAnalyzing = false;
                this.analysisProgress = 0;
            }
        },
        
        async refreshAnalysis() {
            this.refreshing = true;
            
            try {
                // Regenera a análise
                const response = await axios.post(`/api/user/${this.review.cashout.id}/ai-analysis/regenerate`);
                this.aiAnalysis = response.data.analysis;
                
                if (this.$toast) {
                    this.$toast.success('Análise AI atualizada com sucesso!');
                }
            } catch (error) {
                console.error('Failed to refresh AI analysis:', error);
                if (this.$toast) {
                    this.$toast.error('Erro ao atualizar análise AI');
                }
            } finally {
                this.refreshing = false;
            }
        },
        
        formatValue(value) {
            return (value / 100).toLocaleString('pt-BR', { 
                minimumFractionDigits: 2 
            });
        },
        
        getTextRiskColor() {
            if (!this.aiAnalysis) return 'text-gray-500';
            const score = this.aiAnalysis.score;
            
            if (score >= 80) return 'text-green-600';
            if (score >= 60) return 'text-yellow-600';
            if (score >= 30) return 'text-orange-600';
            return 'text-red-600';
        },
        
        getRiskColor() {
            if (!this.aiAnalysis) return 'bg-gray-500';
            const score = this.aiAnalysis.score;
            
            if (score >= 80) return 'bg-green-500';
            if (score >= 60) return 'bg-yellow-500';
            if (score >= 30) return 'bg-orange-500';
            return 'bg-red-500';
        },
        
        getRiskBadgeClass() {
            if (!this.aiAnalysis) return 'bg-gray-100 text-gray-800';
            const score = this.aiAnalysis.score;
            
            if (score >= 80) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
            if (score >= 60) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
            if (score >= 30) return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
        },
        
        getRiskLevel() {
            if (!this.aiAnalysis) return 'DESCONHECIDO';
            
            // Score 100 = Baixo risco, Score 0 = Alto risco
            const score = this.aiAnalysis.score;
            if (score >= 80) return 'MUITO BAIXO';
            if (score >= 60) return 'BAIXO';
            if (score >= 40) return 'MÉDIO';
            if (score >= 20) return 'ALTO';
            return 'CRÍTICO';
        },
        
        getSuggestion() {
            if (!this.aiAnalysis) return 'ANALISAR';
            
            if (this.aiAnalysis.recommends_approval) return 'APROVAR';
            if (this.aiAnalysis.recommends_rejection) return 'REJEITAR';
            return 'REVISAR';
        },
        
        getSuggestionIcon() {
            if (!this.aiAnalysis) return 'ClockIcon';
            
            if (this.aiAnalysis.recommends_approval) return 'CheckCircleIcon';
            if (this.aiAnalysis.recommends_rejection) return 'XCircleIcon';
            return 'ExclamationTriangleIcon';
        },
        
        getSuggestionIconColor() {
            if (!this.aiAnalysis) return 'text-gray-600';
            
            if (this.aiAnalysis.recommends_approval) return 'text-green-600';
            if (this.aiAnalysis.recommends_rejection) return 'text-red-600';
            return 'text-yellow-600';
        },
        
        getSuggestionTextColor() {
            if (!this.aiAnalysis) return 'text-gray-700 dark:text-gray-400';
            
            if (this.aiAnalysis.recommends_approval) return 'text-green-700 dark:text-green-400';
            if (this.aiAnalysis.recommends_rejection) return 'text-red-700 dark:text-red-400';
            return 'text-yellow-700 dark:text-yellow-400';
        },
        
        getTimeColor() {
            const hour = new Date(this.review.created_at).getHours();
            if (hour >= 6 && hour <= 22) return 'text-green-600';
            return 'text-red-600';
        },
        
        getTimeCategory() {
            const hour = new Date(this.review.created_at).getHours();
            if (hour >= 0 && hour <= 5) return 'MADRUGADA';
            if (hour >= 6 && hour <= 11) return 'MANHÃ';
            if (hour >= 12 && hour <= 17) return 'TARDE';
            return 'NOITE';
        },
        
        getFormattedCreationTime() {
            const now = new Date();
            const createdAt = new Date(this.review.created_at);
            const diffInMinutes = Math.floor((now - createdAt) / 1000 / 60);
            
            if (diffInMinutes < 60) {
                return `${diffInMinutes} minutos`;
            } else if (diffInMinutes < 1440) {
                const diffInHours = Math.floor(diffInMinutes / 60);
                return `${diffInHours} horas`;
            } else {
                const diffInDays = Math.floor(diffInMinutes / 1440);
                return `${diffInDays} dias`;
            }
        },
        
        getInsightMetrics() {
            if (!this.aiAnalysis || !this.aiAnalysis.formatted_insights) {
                return { critical: [], warning: [], positive: [] };
            }
            
            return this.aiAnalysis.formatted_insights.metrics || { critical: [], warning: [], positive: [] };
        },
        
        humanizeMetric(metric) {
            const translations = {
                'first_cashout': 'Primeiro saque',
                'high_rejection_ratio': 'Alto índice de rejeição',
                'extremely_high_value': 'Valor extremamente alto',
                'very_high_value': 'Valor muito alto',
                'high_value': 'Valor alto',
                'very_new_account': 'Conta muito nova (< 7 dias)',
                'new_account': 'Conta nova (< 30 dias)',
                'recent_account': 'Conta recente (< 90 dias)',
                'high_cashout_frequency': 'Alta frequência de saques',
                'high_amount_velocity': 'Alta velocidade de valores',
                'high_risk_bin_pattern': 'Concentração de BINs de alto risco',
                'low_bin_diversity_pattern': 'Baixa diversidade de BINs',
                'overnight_sales_pattern': 'Vendas noturnas anormais',
                'round_number_pattern': 'Concentração de valores redondos',
                'related_accounts_pattern': 'Contas relacionadas detectadas',
                'quick_cashout_pattern': 'Saque rápido após atividade',
                'high_chargeback_rate': 'Alta taxa de chargeback',
                'only_credit_card_payments': 'Apenas pagamentos com cartão',
                'has_account_manager': 'Possui gerente de conta',
                'verified_email': 'Email verificado',
                'verified_phone': 'Telefone verificado',
                'mature_account': 'Conta madura (> 1 ano)',
                'established_account': 'Conta estabelecida (> 6 meses)',
                'excellent_cashout_history': 'Excelente histórico de saques',
                'good_cashout_history': 'Bom histórico de saques',
                'moderate_cashout_history': 'Histórico moderado de saques',
                'high_total_sales': 'Alto volume de vendas',
                'low_value_cashout': 'Saque de baixo valor',
                'identical_user_agents': 'User agents idênticos',
                'repeated_ip_addresses': 'IPs repetidos',
                'identical_device_fingerprints': 'Fingerprints de dispositivo idênticos',
                'high_vpn_usage': 'Alto uso de VPN',
                'high_incognito_usage': 'Alto uso de modo incógnito',
                'low_visitor_diversity': 'Baixa diversidade de visitantes',
            };
            
            return translations[metric] || metric.replace(/_/g, ' ');
        },
        
        humanizePattern(pattern) {
            if (typeof pattern === 'string') {
                return this.humanizeMetric(pattern);
            }
            return pattern.type ? this.humanizeMetric(pattern.type) : JSON.stringify(pattern);
        },
        
        canQuickApprove() {
            if (!this.aiAnalysis) return false;
            return this.aiAnalysis.recommends_approval && this.aiAnalysis.confidence >= 0.8;
        },
        
        shouldReject() {
            if (!this.aiAnalysis) return false;
            return this.aiAnalysis.recommends_rejection && this.aiAnalysis.confidence >= 0.8;
        }
    }
};
</script>

<style scoped>
/* Animated stripes for progress bar */
.bg-stripes {
    background-image: linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.15) 25%,
        transparent 25%,
        transparent 50%,
        rgba(255, 255, 255, 0.15) 50%,
        rgba(255, 255, 255, 0.15) 75%,
        transparent 75%,
        transparent
    );
    background-size: 1rem 1rem;
}

@keyframes slide {
    0% {
        background-position: 0 0;
    }
    100% {
        background-position: 1rem 0;
    }
}

.animate-slide {
    animation: slide 1s linear infinite;
}
</style>